import { Typography } from '@workspace/ui/components/Typography';
import { cn } from '@workspace/ui/lib/utils';
import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { company } from '~/company/Company';
import CategoryBlock from '~/components/CategoryBlocks/CategoryBlock';
import { cfSrcForImage } from '~/components/CategoryBlocks/utils';
import Icon from '~/components/Icon/Icon';
import { Breadcrumbs } from '~/components/isolated/Breadcrumbs';
import LanguageSelector from '~/components/LanguageSelector/LanguageSelector';
import { useDateLang } from '~/hooks/useDateLang';
import { useGlobal } from '~/hooks/useGlobal';
import { useStores } from '~/hooks/useStore';
import { useTimeFormat } from '~/hooks/useTimeFormat';
import ShopSidebar from '~/pages/Shop/ShopSidebar';
import { orderStore } from '~/stores';

import MobileHeaderAddress from '../Main/MobileHeader/MobileHeaderAddress';
import MobileHeaderEta from '../Main/MobileHeader/MobileHeaderEta';
import MobileHeaderLayout from '../Main/MobileHeader/MobileHeaderLayout';

import CategorySkeleton from './CategorySkeleton';
import styles from './Shop.module.scss';

const Shop = () => {
  const { isMobile } = useGlobal();
  const { t } = useTranslation();
  const { shopCategories } = useStores();
  const { timeFormat } = useTimeFormat();
  const { locale } = useDateLang();

  const {
    message: expressDeliveryMessage,
    args: expressDeliveryMessageArgs,
    showFlashIcon,
  } = orderStore.getDeliveryLabel({ timeFormat, locale });

  const LabelComponent = isMobile
    ? Typography.Text200Medium
    : Typography.Header100Medium;

  const blocks =
    shopCategories.list.length > 0
      ? shopCategories.list.map((category) =>
          company.match({
            mcosmetic: (
              <Link
                key={category.id}
                to={`/c/${category.id}`}
                className={cn(
                  'flex flex-col items-center justify-between gap-2',
                  'bg-graphic-transparent-brand-10',
                  'nth-[2n]:bg-surface-transparent-2',
                  isMobile
                    ? [
                        'rounded-6 px-4 pt-3',
                        'odd:row-span-3 odd:aspect-160/188 even:row-span-2 even:aspect-160/128',
                        // 1 orphan: lone odd tile → stretch full width
                        '[&:nth-last-child(1):nth-child(4n+1)]:col-span-2 [&:nth-last-child(1):nth-child(4n+1)]:row-span-2 [&:nth-last-child(1):nth-child(4n+1)]:aspect-328/128',
                        // 2 orphans: odd+even, equalize heights
                        '[&:nth-last-child(2):nth-child(4n+1)]:row-span-2 [&:nth-last-child(2):nth-child(4n+1)]:aspect-160/128',
                        // 3 orphans: first odd gets equalized height to match the even beside it
                        '[&:nth-last-child(3):nth-child(4n+1)]:row-span-2 [&:nth-last-child(3):nth-child(4n+1)]:aspect-160/128',
                        // 3 orphans: last odd tile alone in its row → stretch full width
                        '[&:nth-last-child(1):nth-child(4n+3)]:col-span-2 [&:nth-last-child(1):nth-child(4n+3)]:row-span-2 [&:nth-last-child(1):nth-child(4n+3)]:aspect-328/128',
                        'nth-[6n+7]:bg-graphic-transparent-brand-2-15!',
                      ]
                    : 'aspect-338/250 rounded-6.5 nth-[6n+7]:bg-graphic-transparent-brand-2-10! px-8 pt-6',
                )}
              >
                <LabelComponent as="span" className="text-center">
                  {category.name}
                </LabelComponent>
                {category.previewImage && (
                  <div
                    style={{
                      backgroundImage: `url(${cfSrcForImage(category.previewImage)})`,
                    }}
                    className="w-full flex-1 bg-bottom bg-contain bg-no-repeat"
                  />
                )}
              </Link>
            ),
            default: <CategoryBlock key={category.id} category={category} />,
          }),
        )
      : [1, 2, 3, 4, 5, 6].map((i) => <CategorySkeleton key={i} />);

  return (
    <div className={styles.root} data-company={company.name}>
      <Helmet>
        <meta data-react-helmet="true" name="robots" content="index, follow" />
        <title>{t('meta:shopTitle')}</title>
        <meta name="description" content={t('meta:shopDescription')} />
      </Helmet>
      {!(isMobile && company.isMarketplaceVariant) && (
        <MobileHeaderLayout
          className={styles.mobileHeaderLayoutClassName}
          isSticky={true}
          content={
            <>
              <MobileHeaderAddress headerColor />
              <div className={styles.headerGroup}>
                {company.showETAInfoMobileHeader && (
                  <MobileHeaderEta
                    {...{
                      expressDeliveryMessage,
                      expressDeliveryMessageArgs,
                      showFlashIcon,
                    }}
                  />
                )}
                <LanguageSelector
                  dropdownClassNames={styles.languageSelector}
                />
              </div>
            </>
          }
        />
      )}
      <div
        className={classes([
          styles.contentLayout,
          'content-layout',
          company.newDesignSystemEnabled && styles.newDesign,
          company.isMarketplaceVariant && styles.marketplace,
        ])}
      >
        {!isMobile && !company.newDesignSystemEnabled && <ShopSidebar />}
        {company.newDesignSystemEnabled ? (
          <div className={styles.content}>
            {isMobile ? (
              <div className={styles.search}>
                <Link
                  to="/search"
                  className={classes([
                    styles.searchBtn,
                    company.isMarketplaceVariant && styles.searchBtnMarketplace,
                  ])}
                >
                  <Icon type="search-alt" size={16} />
                  <span>{t('header:searchProducts')}</span>
                </Link>
              </div>
            ) : (
              <Breadcrumbs
                className={classes([
                  styles.breadcrumbs,
                  styles.newDesign,
                  company.isMarketplaceVariant && styles.marketplace,
                ])}
                items={[
                  { link: '/', label: t('home') },
                  { label: t('allCategories') },
                ]}
              />
            )}
            <div
              className={company.match({
                mcosmetic:
                  'grid grid-cols-2 gap-2 pt-safe-or-14 md:grid-cols-4 md:gap-4 md:pb-40',
                default: styles.blocksWrapper,
              })}
            >
              {blocks}
            </div>
          </div>
        ) : (
          <div className={styles.blocks}>{blocks}</div>
        )}
      </div>
    </div>
  );
};

export default observer(Shop);
